'use client'

import { Button } from '@/core/components/form/button/button'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import {
  LuCopy,
  LuEllipsis,
  LuImage,
  LuImport,
  LuLayoutGrid,
  LuLink,
  LuList,
  LuPencil,
  LuPlus,
  LuSearch,
  LuTrash2,
} from 'react-icons/lu'

import { useAsyncAction } from '@/core/hooks/use-async-action'
import ErrorToast from '@/core/components/error-toast'
import { InputText } from '@/core/components/form/input-text'
import DropdownMenu from '@/core/components/dropdown-menu'
import { toast } from '@/core/lib/toast'
import { copyToClipboard } from '@/core/utils/clipboard'
import { cn } from '@/core/utils/common'

import { Btn, CoverMedia, Select } from '@/features/studio-renew/components/ui'
import CompletionMeter from '@/features/studio-renew/components/completion-meter'
import StudioStatusBadge from '@/features/studio-renew/components/studio-status-badge'
import EditorBar from '@/features/studio-renew/components/editor-bar'
import ImportPanel from '@/features/studio-renew/components/import-panel'
import StudioTutorial from '@/features/studio-renew/components/studio-tutorial'
import CreatorQuests from '@/features/studio-renew/components/creator-quests'
import CreationRecoveryNotice from '@/features/studio-renew/components/creation-recovery-notice'
import { homeTutorial } from '@/features/studio-renew/lib/studio-tutorial'
import { initialValues } from '@/features/studio-renew/components/steps/status-step'
import CustomScreen from '@/features/veluvit/components/chat/custom-screen'
import WorkTitleOverlay from '@/features/veluvit/components/ui/work-title-overlay'
import { renderScreen } from '@/features/veluvit/lib/custom-screen'
import {
  beginWorkDraft,
  discardWorkDraft,
  ensureStudioCollections,
  hasWorkDraftInput,
  loadStudio,
  savedWorkId,
  useStudioCollectionStatus,
} from '@/features/studio-renew/sync'
import { WORK_DRAFT_ID } from '@/features/studio-renew/lib/work-draft'
import { workCompletion } from '@/features/studio-renew/lib/work-completion'
import { characterCompletion } from '@/features/studio-renew/lib/character-completion'
import { workEntryStep } from '@/features/studio-renew/lib/work-flow'
import {
  canCopyProductLink,
  productLink,
  publicationHint,
  publicationLabel,
} from '@/features/studio-renew/referral'
import {
  isBlankDraft,
  usersOf,
  useRenewStore,
  useStudioServerSnapshot,
} from '@/features/studio-renew/store'
import {
  archiveChat,
  copyChat,
  copyMaterial,
  createCharacter,
  createCommand,
  createStatus,
  createWorld,
  deleteMaterial,
} from '@/features/studio-renew/services/studio-crud.service'
import { chatTitle } from '@/features/studio-renew/steps'
import { TabRow } from '@/features/veluvit/components/ui/layout-parts'
import { openModal } from '@/features/veluvit/stores/overlay.store'

import type { Kind } from '@/features/studio-renew/store'
import type { Character } from '@/features/studio-renew/types'
import type { StudioItemStatus } from '@/features/studio-renew/components/studio-status-badge'
import type { Route } from 'next'
import type { ReactNode } from 'react'

/* 스튜디오 홈 — 메인 앱 헤더 아래 탭 [내 작품] [캐릭터] [세계관] [명령어] [상태창](§14.2 · 2차).
   내 작품은 저장한 초안과 작품, 나머지는 재료. 재료는 묶지 않는다.
   목록은 카드가 기본이고 리스트로 바꿀 수 있다. 탭마다 검색 · 정렬한다.
   명령어 · 상태창은 작품에 연결해 쓰는 원본이라 연결한 작품 수를 보여 준다. */

function ModifiedDate({
  time,
  className,
}: {
  time: number
  className?: string
}) {
  const date = new Date(time)
  const valid = Number.isFinite(date.getTime())
  const full = valid
    ? date.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    : '알 수 없음'
  const short = valid
    ? date
        .toLocaleDateString('en-US', {
          timeZone: 'Asia/Seoul',
          month: '2-digit',
          day: '2-digit',
        })
        .replace('/', '.')
    : '—'
  return (
    <time
      dateTime={valid ? date.toISOString() : undefined}
      title={`최근 수정 ${full}`}
      aria-label={`최근 수정 ${full}`}
      className={cn(
        'shrink-0 whitespace-nowrap text-c12 text-description tabular-nums over10:text-c14',
        className
      )}
    >
      {short}
    </time>
  )
}

/** 목록 한 항목 — 탭마다 다른 엔티티를 이 모양으로 맞춰 카드 · 리스트 · 검색 · 정렬이 한 벌로 돈다 */
type Item = {
  id: string
  href: Route
  title: string
  sub?: string
  time: number
  /** 정렬 ‘많은 순’ 기준(연결 작품 · 소속 캐릭터 수) */
  count?: number
  /** 이미지가 있는 탭(작품 · 캐릭터 · 세계관). 여러 명이면 겹친 얼굴 */
  image?: string
  titleImage?: string
  stack?: { id: string; image: string }[]
  /** 이미지가 없는 탭(명령어 · 상태창)의 대신 그림 */
  mark?: ReactNode
  previewHtml?: string
  chips: ReactNode
  status?: StudioItemStatus
  meta?: string
  completion?: { percent: number; ready: boolean; label?: string }
  busy?: boolean
  onDuplicate: () => void
  onDelete: () => void
  onCopyLink?: () => void
}

function Thumb({
  src,
  stack,
  titleImage,
}: {
  src?: string
  stack?: Item['stack']
  titleImage?: string
}) {
  if (stack && stack.length > 1) {
    return (
      <span className="relative h-10 w-14 flex-none">
        {stack.slice(0, 2).map((s, i) => (
          <span
            key={s.id}
            className={cn(
              'absolute top-0 size-10 overflow-hidden rounded-full border-2 border-surface bg-surface-raised',
              i ? 'left-4' : 'left-0'
            )}
          >
            {s.image && (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={s.image} alt="" className="size-full object-cover" />
            )}
          </span>
        ))}
        <WorkTitleOverlay src={titleImage} sizes="56px" />
      </span>
    )
  }
  return (
    <span className="relative size-10 flex-none overflow-hidden rounded-full bg-surface-raised">
      <CoverMedia src={src} play="still" className="size-full object-cover" />
      <WorkTitleOverlay src={titleImage} sizes="40px" />
    </span>
  )
}

/** 카드 · 줄의 맨 아래 — 상태 · 연결 정보는 왼쪽, 최근 수정일은 오른쪽 끝(모든 탭 같은 순서). 카드(compact)는 상태 · 완성도 한 줄 */
function ItemFooter({ item, compact }: { item: Item; compact?: boolean }) {
  if (compact)
    return (
      <span className="flex min-h-6 min-w-0 items-center justify-between gap-2 text-c12 leading-5 text-description over10:text-c14">
        <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
          {item.status ? (
            <StudioStatusBadge status={item.status} />
          ) : item.meta ? (
            <span className="truncate">{item.meta}</span>
          ) : (
            item.chips
          )}
          {item.status && item.completion && !item.completion.ready && (
            <span className="flex-none tabular-nums">
              {item.completion.percent}%
            </span>
          )}
        </span>
        <ModifiedDate time={item.time} />
      </span>
    )
  return (
    <span className="flex min-h-6 items-center justify-between gap-2 text-c12 leading-5 text-description over10:text-c14">
      <span className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
        {item.status && <StudioStatusBadge status={item.status} />}
        {item.status && (item.meta || item.chips) && (
          <span aria-hidden="true" className="text-t4">
            ·
          </span>
        )}
        {item.meta ? <span className="truncate">{item.meta}</span> : item.chips}
      </span>
      <ModifiedDate time={item.time} />
    </span>
  )
}

/** ⋯ 메뉴 — 편집 · (공개 작품) 링크 복사 · 복제 · 삭제. floating 은 카드 이미지 위 오른쪽 모서리 */
function ItemMenu({ item, floating }: { item: Item; floating?: boolean }) {
  const router = useRouter()
  return (
    <div
      className={floating ? 'absolute top-1.5 right-1.5' : 'relative flex-none'}
    >
      <DropdownMenu
        align="end"
        trigger={
          <span
            className={cn(
              'grid size-8 place-items-center rounded-control',
              floating
                ? 'bg-black/45 text-white backdrop-blur-md hover:bg-black/65'
                : 'text-description hover:bg-surface-raised hover:text-foreground'
            )}
          >
            <LuEllipsis aria-hidden="true" className="size-4" />
            <span className="sr-only">{item.title} 더보기</span>
          </span>
        }
        items={[
          {
            id: 'edit',
            label: '편집',
            icon: <LuPencil aria-hidden="true" className="size-4" />,
            onClick: () => router.push(item.href),
          },
          ...(item.onCopyLink
            ? [
                {
                  id: 'link',
                  label: '작품 링크 복사',
                  icon: <LuLink aria-hidden="true" className="size-4" />,
                  onClick: item.onCopyLink,
                },
              ]
            : []),
          {
            id: 'duplicate',
            label: '복제',
            icon: <LuCopy aria-hidden="true" className="size-4" />,
            onClick: item.onDuplicate,
            disabled: item.busy,
          },
          {
            id: 'delete',
            label: '삭제',
            icon: <LuTrash2 aria-hidden="true" className="size-4" />,
            onClick: item.onDelete,
            disabled: item.busy,
            danger: true,
          },
        ]}
      />
    </div>
  )
}

/** 리스트 한 줄 — 그룹 면 안, 64px */
function Row({ item, hideMedia = false }: { item: Item; hideMedia?: boolean }) {
  return (
    <li
      data-studio-tour="first-item"
      className="group flex min-h-16 items-center gap-3 px-3 py-4 transition-colors duration-150 hover:bg-surface-raised/50 over10:px-4"
    >
      <Link
        href={item.href}
        prefetch={false}
        className="flex min-w-0 flex-1 items-center gap-3 no-underline outline-none"
      >
        {!hideMedia &&
          (item.previewHtml ? (
            <span
              className="pointer-events-none block h-14 w-24 flex-none overflow-hidden rounded-field bg-[#0e0d12] p-1"
              aria-hidden="true"
            >
              <span className="block w-[320px] origin-top-left scale-[0.275]">
                <CustomScreen
                  title={`${item.title} 미리보기`}
                  html={item.previewHtml}
                />
              </span>
            </span>
          ) : item.mark ? (
            <span className="grid size-10 flex-none place-items-center rounded-field bg-surface-raised">
              {item.mark}
            </span>
          ) : (
            <Thumb
              src={item.image}
              stack={item.stack}
              titleImage={item.titleImage}
            />
          ))}
        <span className="min-w-0 flex-1">
          <span
            title={item.title}
            className="block min-w-0 truncate text-c15 leading-6 font-semibold text-foreground underline-offset-4 group-focus-within:underline over10:text-c17"
          >
            {item.title}
          </span>
          <span className="mt-0.5 block min-h-5 truncate text-c13 leading-5 text-description over10:text-c15 over10:leading-6">
            {item.sub?.trim() || '소개가 아직 없어요.'}
          </span>
          {item.completion && (
            <span className="mt-2 block max-w-60">
              <CompletionMeter
                value={item.completion}
                label={item.completion.label || '작품 완성도'}
              />
            </span>
          )}
          <span className="mt-2 block over6:hidden">
            <ItemFooter item={item} />
          </span>
        </span>
      </Link>
      <span className="hidden w-64 flex-none over6:block">
        <ItemFooter item={item} />
      </span>
      <ItemMenu item={item} />
    </li>
  )
}

type Media = 'portrait' | 'wide' | 'none'

/** 카드 보기 — 상자 없이 이미지 + 아래 글(애플 다크 결). 둥근 건 이미지 칸뿐, 테두리 · 면 없음 */
function CardItem({ item, media }: { item: Item; media: Media }) {
  return (
    <li data-studio-tour="first-item" className="group relative h-full min-w-0">
      <Link
        href={item.href}
        prefetch={false}
        className="flex h-full min-w-0 flex-col no-underline outline-none"
      >
        {media !== 'none' ? (
          <div
            className={cn(
              'relative w-full shrink-0 overflow-hidden rounded-panel bg-surface transition-[filter] duration-200 group-hover:brightness-110 group-focus-within:brightness-110',
              media === 'portrait' ? 'aspect-[2/3]' : 'aspect-video'
            )}
          >
            {item.stack && item.stack.length > 1 ? (
              <div className="absolute inset-0 grid size-full grid-cols-2">
                {item.stack.slice(0, 2).map((s) => (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    key={s.id}
                    src={s.image || undefined}
                    alt=""
                    className="size-full object-cover"
                  />
                ))}
              </div>
            ) : item.image ? (
              // 움직이는 표지는 올렸을 때만 재생 — 카드 여러 장이 한꺼번에 돌지 않게
              <CoverMedia
                src={item.image}
                play="hover"
                className="absolute inset-0 size-full object-cover object-[50%_20%]"
              />
            ) : (
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-description">
                <LuImage aria-hidden="true" className="size-7" />
                <span className="text-c13 over10:text-c14">이미지 없음</span>
              </span>
            )}
            <WorkTitleOverlay src={item.titleImage} />
            {item.completion && !item.completion.ready && (
              <span
                role="progressbar"
                aria-label={item.completion.label || '작품 완성도'}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={item.completion.percent}
                className="absolute inset-x-0 bottom-0 h-1 bg-black/50"
              >
                <span
                  className="block h-full bg-point"
                  style={{ width: `${item.completion.percent}%` }}
                />
              </span>
            )}
          </div>
        ) : item.previewHtml ? (
          <div
            className="pointer-events-none aspect-[4/3] overflow-hidden rounded-panel bg-[#0e0d12] p-3"
            aria-hidden="true"
          >
            <span className="block w-[320px] origin-top-left scale-[0.7]">
              <CustomScreen
                title={`${item.title} 미리보기`}
                html={item.previewHtml}
              />
            </span>
          </div>
        ) : (
          <div className="grid aspect-[4/3] place-items-center rounded-panel bg-surface text-c24 transition-[filter] duration-200 group-hover:brightness-125">
            {item.mark}
          </div>
        )}
        {/* 제목 → 소개 한 줄 → 상태 · 완성도 · 날짜 한 줄. 완성도 막대는 이미지 아래 끝에 붙인다 */}
        <div className="flex min-w-0 flex-1 flex-col pt-3">
          <div
            title={item.title}
            className="h-6 truncate text-c15 leading-6 font-semibold text-foreground underline-offset-4 group-focus-within:underline over10:text-c17"
          >
            {item.title}
          </div>
          <div className="mt-0.5 h-5 truncate text-c13 leading-5 text-description over10:h-6 over10:text-c15 over10:leading-6">
            {item.sub?.trim() || '한 줄 소개를 작성해 주세요.'}
          </div>
          <div className="mt-auto pt-1.5">
            <ItemFooter item={item} compact />
          </div>
        </div>
      </Link>
      <ItemMenu item={item} floating />
    </li>
  )
}

type Create = { label: string; hint: string; run: () => void; busy?: boolean }

const CREATE_DASHED =
  'border-2 border-dashed border-foreground/25 transition-colors duration-150'

/** 목록의 첫 칸은 항상 만들기 — 카드 보기는 카드 이미지와 같은 크기의 점선 칸 */
function CreateCard({ create, media }: { create: Create; media: Media }) {
  return (
    <li className="min-w-0">
      <Button
        type="button"
        data-studio-tour="create"
        disabled={create.busy}
        onClick={create.run}
        className={cn(
          CREATE_DASHED,
          'flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-panel text-c14 font-semibold text-description outline-none enabled:hover:border-point enabled:hover:text-foreground focus-visible:border-point focus-visible:bg-surface-raised focus-visible:text-foreground disabled:cursor-not-allowed disabled:opacity-50 over10:text-c15',
          media === 'portrait'
            ? 'aspect-[2/3]'
            : media === 'wide'
              ? 'aspect-video'
              : 'aspect-[4/3]'
        )}
        variant="bare"
      >
        <LuPlus aria-hidden="true" className="size-6" />
        {create.label}
      </Button>
    </li>
  )
}

/** 리스트 보기의 첫 줄 — 만들기 */
function CreateRow({ create }: { create: Create }) {
  return (
    <li>
      <Button
        type="button"
        data-studio-tour="create"
        disabled={create.busy}
        onClick={create.run}
        className="group flex min-h-16 w-full cursor-pointer items-center gap-3 px-3 py-4 text-left outline-none transition-colors duration-150 enabled:hover:bg-surface-raised/50 focus-visible:bg-surface-raised disabled:cursor-not-allowed disabled:opacity-50 over10:px-4"
        variant="bare"
      >
        <span
          className={cn(
            CREATE_DASHED,
            'grid size-10 flex-none place-items-center rounded-full text-description group-enabled:group-hover:border-point group-enabled:group-hover:text-foreground'
          )}
        >
          <LuPlus aria-hidden="true" className="size-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-c15 leading-6 font-semibold text-foreground over10:text-c17">
            {create.label}
          </span>
          <span className="mt-0.5 block truncate text-c13 leading-5 text-description over10:text-c15 over10:leading-6">
            {create.hint}
          </span>
        </span>
      </Button>
    </li>
  )
}

type Tab = 'works' | 'character' | 'world' | 'command' | 'status'
const TABS: { k: Tab; label: string; href: Route }[] = [
  { k: 'works', label: '내 작품', href: '/studio-renew' as Route },
  {
    k: 'character',
    label: '캐릭터',
    href: '/studio-renew?tab=character' as Route,
  },
  { k: 'world', label: '세계관', href: '/studio-renew?tab=world' as Route },
  { k: 'command', label: '명령어', href: '/studio-renew?tab=command' as Route },
  { k: 'status', label: '상태창', href: '/studio-renew?tab=status' as Route },
]
const isTab = (v: string | null): v is Tab => TABS.some((t) => t.k === v)

/* 카드 격자 — 화면 폭을 다 쓰게 되면서(2026-10-07) PC 는 칸 수를 고정하지 않고 폭에 맞춰 채운다(홈 피드와 같은 방식) */
const GRID_PORTRAIT =
  'grid-cols-2 over6:grid-cols-3 over10:grid-cols-[repeat(auto-fill,minmax(184px,1fr))]'
const GRID_WIDE =
  'grid-cols-1 over6:grid-cols-2 over10:grid-cols-[repeat(auto-fill,minmax(300px,1fr))]'
const GRID_PLAIN =
  'grid-cols-2 over6:grid-cols-3 over10:grid-cols-[repeat(auto-fill,minmax(240px,1fr))]'

/** 탭마다 — 단위 이름 · 카드 이미지 모양 · ‘많은 순’ 정렬 이름(없으면 그 정렬 없음) */
const TAB_META: Record<
  Tab,
  { unit: string; media: Media; countLabel?: string; grid: string }
> = {
  works: { unit: '작품', media: 'portrait', grid: GRID_PORTRAIT },
  character: {
    unit: '캐릭터',
    media: 'portrait',
    countLabel: '작품 많은 순',
    grid: GRID_PORTRAIT,
  },
  world: {
    unit: '세계관',
    media: 'wide',
    countLabel: '캐릭터 많은 순',
    grid: GRID_WIDE,
  },
  command: {
    unit: '명령어',
    media: 'none',
    countLabel: '작품 많은 순',
    grid: GRID_PLAIN,
  },
  status: {
    unit: '상태창',
    media: 'none',
    countLabel: '작품 많은 순',
    grid: GRID_PLAIN,
  },
}

/** 주격 조사 — 받침이 있으면 ‘이’, 없으면 ‘가’(작품이 · 캐릭터가) */
const withSubject = (word: string) => {
  const code = word.charCodeAt(word.length - 1) - 0xac00
  return `${word}${code >= 0 && code <= 11171 && code % 28 ? '이' : '가'}`
}

type Sort = 'recent' | 'name' | 'count'
type View = 'card' | 'list'

/** 검색 · 정렬 · 카드/리스트 — 탭을 바꾸면 검색어와 정렬은 새로 시작(key) */
function ItemList({
  tab,
  items,
  empty,
  view,
  onView,
  create,
  loading,
}: {
  tab: Tab
  items: Item[]
  empty: ReactNode
  view: View
  onView: (v: View) => void
  create: Create
  loading: boolean
}) {
  const meta = TAB_META[tab]
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<Sort>('recent')

  const needle = q.trim().toLowerCase()
  const shown = items
    .filter(
      (it) =>
        !needle || `${it.title} ${it.sub ?? ''}`.toLowerCase().includes(needle)
    )
    .sort((a, b) =>
      sort === 'name'
        ? a.title.localeCompare(b.title, 'ko')
        : sort === 'count'
          ? (b.count ?? 0) - (a.count ?? 0) || b.time - a.time
          : b.time - a.time
    )

  const hasItems = items.length > 0

  return (
    <section aria-label={`${meta.unit} 목록`} aria-busy={loading}>
      <div className="mb-5">
        <div className="flex min-w-0 items-center gap-2">
          <div
            data-studio-tour="search"
            className="relative min-w-0 flex-1 over8:max-w-80"
          >
            <LuSearch
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-4 z-[1] size-4 -translate-y-1/2 text-description"
            />
            <InputText
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`${meta.unit} 이름 · 소개로 찾기`}
              aria-label={`${meta.unit} 검색`}
              className="pl-11"
            />
          </div>
          <div
            data-studio-tour="list-tools"
            className="flex flex-none items-center gap-1"
          >
            <Select
              variant="ghost"
              label="정렬"
              value={sort}
              onChange={setSort}
              options={[
                { v: 'recent' as const, label: '최근 수정 순' },
                { v: 'name' as const, label: '이름순' },
                ...(meta.countLabel
                  ? [{ v: 'count' as const, label: meta.countLabel }]
                  : []),
              ]}
            />
            {tab !== 'command' && (
              <div role="group" aria-label="보기 방식" className="flex gap-1">
                <Btn
                  tone="ghost"
                  size="icon"
                  aria-pressed={view === 'card'}
                  aria-label="카드로 보기"
                  onClick={() => onView('card')}
                  className={
                    view === 'card'
                      ? 'bg-surface-raised! text-foreground!'
                      : 'text-description!'
                  }
                >
                  <LuLayoutGrid aria-hidden="true" className="size-5" />
                </Btn>
                <Btn
                  tone="ghost"
                  size="icon"
                  aria-pressed={view === 'list'}
                  aria-label="리스트로 보기"
                  onClick={() => onView('list')}
                  className={
                    view === 'list'
                      ? 'bg-surface-raised! text-foreground!'
                      : 'text-description!'
                  }
                >
                  <LuList aria-hidden="true" className="size-5" />
                </Btn>
              </div>
            )}
          </div>
        </div>
      </div>
      <h2 className="mb-3 text-c13 font-semibold text-description tabular-nums over10:text-c14">
        {meta.unit}{' '}
        {loading
          ? '—'
          : shown.length === items.length
            ? items.length
            : `${shown.length} / ${items.length}`}
      </h2>
      {tab !== 'command' && view === 'card' ? (
        <ul className={cn('grid gap-x-3 gap-y-5 over10:gap-x-4', meta.grid)}>
          <CreateCard create={create} media={meta.media} />
          {shown.map((it) => (
            <CardItem key={it.id} item={it} media={meta.media} />
          ))}
        </ul>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-panel bg-surface">
          <CreateRow create={create} />
          {shown.map((it) => (
            <Row key={it.id} item={it} hideMedia={tab === 'command'} />
          ))}
        </ul>
      )}
      {!hasItems && !loading && empty && (
        <p
          data-studio-tour="empty"
          className="py-8 text-c15 leading-relaxed text-description"
        >
          {empty}
        </p>
      )}
      {hasItems && shown.length === 0 && (
        <p className="py-16 text-center text-c15 text-description">
          {needle
            ? `‘${q.trim()}’에 맞는 ${withSubject(meta.unit)} 없어요.`
            : '이 상태의 작품이 없어요.'}
        </p>
      )}
    </section>
  )
}

export default function HomeScreen() {
  const initial = useStudioServerSnapshot()
  const router = useRouter()
  const params = useSearchParams()
  const tabParam = params.get('tab')
  const tab: Tab = isTab(tabParam) ? tabParam : 'works'
  const chats = useRenewStore((s) => s.chats)
  const characters = useRenewStore((s) => s.characters)
  const worlds = useRenewStore((s) => s.worlds)
  const statuses = useRenewStore((s) => s.statuses)
  const commands = useRenewStore((s) => s.commands)
  const { busy: actionBusy, run } = useAsyncAction()
  const runAction = useCallback(
    async (action: () => Promise<void>) => {
      try {
        await run(action)
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : '서버에서 작업을 마치지 못했어요.'
        )
      }
    },
    [run]
  )

  const owner = useRenewStore((s) => s.owner)
  const collection: Kind = tab === 'works' ? 'chat' : tab
  const currentLoadState = useStudioCollectionStatus(
    (state) => state.byKind[collection]
  )
  const loadState =
    initial?.readyKinds.includes(collection) && currentLoadState === 'idle'
      ? 'ready'
      : currentLoadState
  const loadError = useStudioCollectionStatus(
    (state) => state.error[collection]
  )
  const charactersReady = useStudioCollectionStatus(
    (state) => state.byKind.character === 'ready'
  )
  const charactersLoaded =
    charactersReady || !!initial?.readyKinds.includes('character')
  const chatsReady = useStudioCollectionStatus(
    (state) => state.byKind.chat === 'ready'
  )
  const chatsLoaded = chatsReady || !!initial?.readyKinds.includes('chat')
  // 탭을 옮길 때 불러오는 화면이 끼지 않게 모든 탭의 목록을 미리 읽는다
  useEffect(() => {
    if (owner)
      void ensureStudioCollections([
        'chat',
        'character',
        'world',
        'command',
        'status',
      ]).catch(() => {})
  }, [owner, collection])

  const [view, setView] = useState<View>('card')
  const changeView = (v: View) => {
    setView(v)
  }

  const chatList = Object.values(chats)
    .filter((work) => work.id !== WORK_DRAFT_ID || hasWorkDraftInput(work))
    .sort((a, b) => b.updatedAt - a.updatedAt)
  const charList = Object.values(characters)
    .filter((item) => !isBlankDraft('character', item))
    .sort((a, b) => b.updatedAt - a.updatedAt)
  const worldList = Object.values(worlds)
    .filter((item) => !isBlankDraft('world', item))
    .sort((a, b) => b.updatedAt - a.updatedAt)
  const commandList = Object.values(commands)
    .filter((item) => !isBlankDraft('command', item))
    .sort((a, b) => b.updatedAt - a.updatedAt)
  const statusList = Object.values(statuses)
    .filter((item) => !isBlankDraft('status', item))
    .sort((a, b) => b.updatedAt - a.updatedAt)
  const worksOf = (id: string) =>
    chatList.filter((c) => c.characterIds.includes(id)).length

  const del = (kind: Kind, id: string, label: string, note = '') => {
    if (!window.confirm(`${label}을(를) 삭제할까요?${note} 되돌릴 수 없어요.`))
      return
    void runAction(async () => {
      if (kind === 'chat' && id === WORK_DRAFT_ID) discardWorkDraft()
      else if (kind === 'chat') await archiveChat(id)
      else await deleteMaterial(kind, id)
      toast.show(
        kind === 'chat' && id === WORK_DRAFT_ID
          ? '초안 삭제 완료'
          : kind === 'chat'
            ? '작품 삭제 완료'
            : '삭제 완료'
      )
    })
  }
  /** 연결한 작품이 있으면 삭제 확인에 알린다 — 작품에서는 빠지기만 한다 */
  const linkedNote = (n: number) => (n ? ` 연결한 작품 ${n}개에서 빠져요.` : '')
  const dup = (kind: Kind, id: string) => {
    void runAction(async () => {
      if (kind === 'chat') await copyChat(await savedWorkId(id))
      else await copyMaterial(kind, id)
      toast.show('복제했어요')
    })
  }
  const newWork = () => {
    void runAction(async () => {
      const id = beginWorkDraft()
      router.push(`/studio-renew/work/${id}/intro` as Route)
    })
  }
  const create: Record<Tab, Create> = {
    works: {
      label: '새 작품',
      hint: '소개 · 캐릭터 · 시작 장면',
      run: newWork,
      busy: actionBusy,
    },
    character: {
      label: '새 캐릭터',
      hint: '이름 · 이미지 · 설정',
      run: () =>
        void runAction(async () => {
          const id = await createCharacter()
          router.push(`/studio-renew/character/${id}/profile` as Route)
        }),
      busy: actionBusy,
    },
    world: {
      label: '새 세계관',
      hint: '캐릭터들이 사는 곳',
      run: () =>
        void runAction(async () => {
          const id = await createWorld()
          router.push(`/studio-renew/world/${id}/basic` as Route)
        }),
      busy: actionBusy,
    },
    command: {
      label: '새 명령어',
      hint: '!폰처럼 대화 중 부르는 화면',
      run: () =>
        void runAction(async () => {
          const id = await createCommand()
          router.push(`/studio-renew/command/${id}/edit` as Route)
        }),
      busy: actionBusy,
    },
    status: {
      label: '새 상태창',
      hint: '호감도 · 장소 같은 값',
      run: () =>
        void runAction(async () => {
          const id = await createStatus()
          router.push(`/studio-renew/status/${id}/edit` as Route)
        }),
      busy: actionBusy,
    },
  }

  const items = (): Item[] => {
    if (tab === 'works') {
      return chatList.map((c) => {
        const cast = c.characterIds
          .map((id) => characters[id])
          .filter((x): x is Character => !!x)
        const portraits = cast.filter((character) => !!character.image?.trim())
        const title = chatTitle(c, characters)
        const visibilityLabel = publicationLabel(c)
        const completion = charactersLoaded
          ? workCompletion(c, characters)
          : undefined
        return {
          id: c.id,
          href: `/studio-renew/work/${c.id}/${charactersLoaded ? workEntryStep(c, characters) : 'intro'}` as Route,
          title,
          sub:
            cast
              .map((x) => x.name)
              .filter(Boolean)
              .join(' · ') || '캐릭터 없음',
          time: c.updatedAt,
          image:
            c.cover || characters[c.lead || '']?.image || portraits[0]?.image,
          titleImage: c.titleImage,
          stack:
            portraits.length > 1 && !c.cover
              ? portraits.map((x) => ({ id: x.id, image: x.image }))
              : undefined,
          completion,
          chips: null,
          status: {
            label: c.publication?.published
              ? visibilityLabel
              : completion?.ready
                ? '필수 작성 완료'
                : '작성 중',
            title: publicationHint(c),
            tone: c.publication?.published
              ? 'published'
              : completion?.ready
                ? 'ready'
                : 'draft',
          } satisfies NonNullable<Item['status']>,
          onDuplicate: () => dup('chat', c.id),
          onDelete: () => del('chat', c.id, title),
          onCopyLink: canCopyProductLink(c)
            ? () =>
                void productLink(c)
                  .then(copyToClipboard)
                  .then(() =>
                    toast.show(
                      '작품 링크를 복사했어요 · 이 링크로 들어온 이용분은 정산이 더 쳐져요'
                    )
                  )
                  .catch((error: unknown) =>
                    toast.show(
                      error instanceof Error
                        ? error.message
                        : '작품 링크를 만들지 못했어요'
                    )
                  )
            : undefined,
        }
      })
    }
    if (tab === 'character') {
      return charList.map((c) => {
        const n = worksOf(c.id)
        const completion = characterCompletion(c)
        return {
          id: c.id,
          href: `/studio-renew/character/${c.id}/${completion.nextStep}` as Route,
          title: c.name || '이름 없는 캐릭터',
          sub: c.tagline,
          time: c.updatedAt,
          count: chatsLoaded ? n : undefined,
          image: c.image,
          completion: { ...completion, label: '캐릭터 완성도' },
          status: {
            label: completion.ready ? '필수 작성 완료' : '작성 중',
            tone: completion.ready ? 'ready' : 'draft',
          } satisfies NonNullable<Item['status']>,
          chips: null,
          meta: chatsLoaded ? `연결 작품 ${n}개` : '연결 작품 —',
          onDuplicate: () => dup('character', c.id),
          onDelete: () => del('character', c.id, c.name || '캐릭터'),
        }
      })
    }
    if (tab === 'world') {
      return worldList.map((w) => {
        const n = charactersLoaded
          ? charList.filter((c) => c.worldId === w.id).length
          : null
        return {
          id: w.id,
          href: `/studio-renew/world/${w.id}/basic` as Route,
          title: w.name || '이름 없는 세계관',
          sub: w.summary,
          time: w.updatedAt,
          count: n ?? undefined,
          image: w.cover,
          chips: null,
          meta: [
            n !== null ? `캐릭터 ${n}명` : null,
            w.visibility === 'public' ? '공개' : '나만 보기',
          ]
            .filter(Boolean)
            .join(' · '),
          onDuplicate: () => dup('world', w.id),
          onDelete: () => del('world', w.id, w.name || '세계관'),
        }
      })
    }
    if (tab === 'command') {
      return commandList.map((c) => {
        const n = usersOf(chats, 'command', c.id).length
        return {
          id: c.id,
          href: `/studio-renew/command/${c.id}/edit` as Route,
          title: `${c.key || '!키 없음'} ${c.title}`.trim(),
          sub: c.description,
          time: c.updatedAt,
          count: n,
          mark: <span className="font-bold text-rose">!</span>,
          chips: null,
          meta: `연결 작품 ${n}개`,
          onDuplicate: () => dup('command', c.id),
          onDelete: () =>
            del('command', c.id, c.key || '명령어', linkedNote(n)),
        }
      })
    }
    return statusList.map((s) => {
      const n = usersOf(chats, 'status', s.id).length
      const visible = s.fields.filter((f) => !f.hidden && f.key)
      return {
        id: s.id,
        href: `/studio-renew/status/${s.id}/edit` as Route,
        title: s.name || '이름 없는 상태창',
        sub: visible.map((f) => f.key).join(' · '),
        time: s.updatedAt,
        count: n,
        mark: (
          <span className="font-semibold text-t3 tabular-nums">
            {visible.length}
          </span>
        ),
        previewHtml: s.html.trim()
          ? renderScreen(s.html, initialValues(s.fields))
          : undefined,
        chips: null,
        meta: `연결 작품 ${n}개`,
        onDuplicate: () => dup('status', s.id),
        onDelete: () => del('status', s.id, s.name || '상태창', linkedNote(n)),
      }
    })
  }

  const EMPTY: Record<Tab, string> = {
    works: '',
    character: '아직 캐릭터가 없어요.',
    world: '세계관은 선택이에요. 여러 캐릭터가 같은 배경을 공유할 때 만들어요.',
    command: '',
    status:
      '호감도 · 장소처럼 대화 중 바뀌는 값을 보여 주는 화면이에요. 만들어 두면 여러 작품에 연결해 쓸 수 있어요.',
  }

  const meta = TAB_META[tab]
  const toolsOff = create[tab].busy || loadState !== 'ready'
  const importNew = () =>
    openModal(
      <ImportPanel target={{ kind: tab === 'works' ? 'work' : tab }} />,
      {
        label: `${meta.unit} 이식하기`,
        key: `studio-import:new:${tab}`,
        className: 'max-w-[680px]!',
      }
    )

  return (
    <div className="pb-24 text-foreground">
      <EditorBar
        back={'/' as Route}
        backLabel="홈으로"
        title="스튜디오"
        thumb={null}
        below={
          <div data-studio-tour="tabs">
            <TabRow
              wide
              items={TABS.map((t) => ({
                key: t.k,
                label: t.label,
                href: t.href,
                prefetch: false,
                onNavigate: (event) => {
                  event.preventDefault()
                  window.history.pushState(null, '', t.href)
                },
                on: tab === t.k,
              }))}
            />
          </div>
        }
      >
        <Btn
          data-studio-tour="import"
          tone="ghost"
          size="sm"
          disabled={toolsOff}
          onClick={importNew}
          className="flex-none whitespace-nowrap"
        >
          <LuImport aria-hidden="true" className="size-4 flex-none" />
          이식하기
        </Btn>
        <StudioTutorial
          steps={() => homeTutorial(tab === 'works' ? 'work' : tab)}
          disabled={toolsOff}
        />
      </EditorBar>
      <div className="px-4 over10:px-8">
        <main className="py-6">
          <CreationRecoveryNotice />
          {owner && (
            <CreatorQuests
              key={owner}
              tab={tab}
              onCreate={(kind) => create[kind].run()}
            />
          )}
          {loadState === 'error' ? (
            <div className="py-12 text-center">
              <ErrorToast
                message={
                  loadError || `${TAB_META[tab].unit} 목록을 불러오지 못했어요.`
                }
              />
              <Btn
                onClick={() => {
                  if (owner)
                    void loadStudio(owner, [collection]).catch(() => {})
                }}
              >
                다시 불러오기
              </Btn>
            </div>
          ) : (
            <ItemList
              key={tab}
              tab={tab}
              items={
                loadState === 'ready'
                  ? items().map((item) => ({ ...item, busy: actionBusy }))
                  : []
              }
              empty={EMPTY[tab]}
              view={view}
              onView={changeView}
              create={{
                ...create[tab],
                busy: create[tab].busy || loadState !== 'ready',
              }}
              loading={loadState !== 'ready'}
            />
          )}
        </main>
      </div>
    </div>
  )
}
